import React, {useState, useEffect, useRef} from 'react';
import Editor from '@monaco-editor/react';
import {load} from 'js-yaml';
import {useProgress} from '@/hooks/useProgress';
import {useNav} from '@/hooks/useNav';
import {r} from "@/utils/responsive";
import ValidationResult from './ValidationResult';
import {MaskShape, useMaskShapes} from "@/hooks/useMaskShapes";
import DisabledWrapper from "@/components/DisabledWrapper";
import EditorBlurShapes from "@/components/EditorBlurShapes";
import gridPattern from "@/assets/img/grid_pattern.svg";
import BotHelperButton from "@/components/BotHelperButton";
import {configureMonacoYaml, MonacoYamlOptions} from 'monaco-yaml'

interface EditorProps extends React.HTMLAttributes<HTMLDivElement> {
}

const K8S_SCHEMA = {
    "description": "Deployment enables declarative updates for Pods and ReplicaSets.",
    "properties": {
        "apiVersion": {
            "description": "APIVersion defines the versioned schema of this representation of an object. Servers should convert recognized schemas to the latest internal value, and may reject unrecognized values. More info: https://git.k8s.io/community/contributors/devel/api-conventions.md#resources",
            "type": [
                "string",
                "null"
            ],
            "enum": [
                "apps/v1"
            ]
        },
        "kind": {
            "description": "Kind is a string value representing the REST resource this object represents. Servers may infer this from the endpoint the client submits requests to. Cannot be updated. In CamelCase. More info: https://git.k8s.io/community/contributors/devel/api-conventions.md#types-kinds",
            "type": [
                "string",
                "null"
            ],
            "enum": [
                "Deployment"
            ]
        },
        "metadata": {
            "$ref": "https://kubernetesjsonschema.dev/v1.14.0/_definitions.json#/definitions/io.k8s.apimachinery.pkg.apis.meta.v1.ObjectMeta",
            "description": "Standard object metadata."
        },
        "spec": {
            "$ref": "https://kubernetesjsonschema.dev/v1.14.0/_definitions.json#/definitions/io.k8s.api.apps.v1.DeploymentSpec",
            "description": "Specification of the desired behavior of the Deployment."
        },
        "status": {
            "$ref": "https://kubernetesjsonschema.dev/v1.14.0/_definitions.json#/definitions/io.k8s.api.apps.v1.DeploymentStatus",
            "description": "Most recently observed status of the Deployment."
        }
    },
    "type": "object",
    "x-kubernetes-group-version-kind": [
        {
            "group": "apps",
            "kind": "Deployment",
            "version": "v1"
        }
    ],
    "$schema": "http://json-schema.org/schema#"
};

const editorMaskShapes: MaskShape[] = [
    {
        width: 1226,
        height: 100,
        position: 'top',
        path: "M1146 0C1183.71 0 1202.57 0.000106813 1214.28 11.7158C1225.92 23.3473 1226 42.0169 1226 79.1895V100H0C0.394457 75.825 2.308 62.0191 11.6113 52.7158C23.3271 41.0001 42.1832 41 79.8955 41H518.094C530.97 41 537.409 40.9999 543.363 39.0918C545.308 38.4687 547.202 37.6974 549.028 36.7852C554.622 33.9915 559.23 29.4944 568.444 20.5C577.659 11.5059 582.266 7.00851 587.859 4.21484C589.686 3.30258 591.58 2.53131 593.524 1.9082C599.479 7.31445e-05 605.917 0 618.794 0H1146Z"
    },
    {
        position: 'middle',
        fluid: true
    },
    {
        width: 1226,
        height: 150,
        position: 'bottom',
        path: "M1226 26.0371C1226 37.6876 1226 43.5129 1224.38 49.0254C1222.77 54.538 1219.62 59.441 1213.33 69.2461L1185.11 113.21C1173.56 131.205 1167.79 140.203 1158.82 145.102C1149.86 150 1139.17 150 1117.79 150H80C42.2876 150 23.4315 150 11.7158 138.284C0.000106812 126.568 0 107.712 0 70V0H1226V26.0371Z",
    }
]

const editorMaskBorderShapes: MaskShape[] = [
    {
        width: 1226,
        height: 100,
        position: 'top',
        path: "M1146 0C1183.71 0 1202.57 0.000106813 1214.28 11.7158C1225.92 23.3473 1226 42.0169 1226 79.1895V100H0C0.394457 75.825 2.308 62.0191 11.6113 52.7158C23.3271 41.0001 42.1832 41 79.8955 41H518.094C530.97 41 537.409 40.9999 543.363 39.0918C545.308 38.4687 547.202 37.6974 549.028 36.7852C554.622 33.9915 559.23 29.4944 568.444 20.5C577.659 11.5059 582.266 7.00851 587.859 4.21484C589.686 3.30258 591.58 2.53131 593.524 1.9082C599.479 7.31445e-05 605.917 0 618.794 0H1146Z",
        inverted: true,
    },
    {
        position: 'middle',
        inverted: true,
        fluid: true
    },
    {
        width: 1226,
        height: 150,
        position: 'bottom',
        inverted: true,
        path: "M1226 26.0371C1226 37.6876 1226 43.5129 1224.38 49.0254C1222.77 54.538 1219.62 59.441 1213.33 69.2461L1185.11 113.21C1173.56 131.205 1167.79 140.203 1158.82 145.102C1149.86 150 1139.17 150 1117.79 150H80C42.2876 150 23.4315 150 11.7158 138.284C0.000106812 126.568 0 107.712 0 70V0H1226V26.0371Z",
    }
]


const CodeEditor: React.FC<EditorProps> = ({className, ...props}) => {
    const {
        currentYaml,
        setCurrentYaml,
        validationResult,
        clearValidationResult,
        nextStepWithTask,
        currentTask,
        submitSolution,
        isLoading,
    } = useProgress();
    const {navigateToSection} = useNav();
    const [showValidationResult, setShowValidationResult] = useState(true);
    const editorRef = useRef<HTMLDivElement>(null);
    const editorBorderRef = useRef<HTMLDivElement>(null);
    const monacoRef = useRef<any>(null);
    const editorInstanceRef = useRef<any>(null);
    const decorationsRef = useRef<any[]>([]);
    const submitRef = useRef({ currentYaml, currentTask, isLoading, submitSolution });

    useEffect(() => {
        submitRef.current = { currentYaml, currentTask, isLoading, submitSolution };
    }, [currentYaml, currentTask, isLoading, submitSolution]);

    // Получаем SVG элемент с масками
    const {
        svgJSX,
        clipPath
    } = useMaskShapes(editorMaskShapes, 'editor-mask', editorRef);

    const {
        svgJSX: svgJSXBorder,
        clipPath: clipPathBorder,
    } = useMaskShapes(editorMaskBorderShapes, 'editor-border-mask', editorBorderRef);

    const handleNextTask = async () => {
        navigateToSection('briefing');
        await nextStepWithTask();
        setShowValidationResult(false);
    };

    // Показываем результат валидации когда он появляется
    useEffect(() => {
        if (validationResult) {
            setShowValidationResult(true);
        }
    }, [validationResult]);

    const handleEditorChange = (value: string | undefined) => {
        setCurrentYaml(value || null);
        // Очищаем результат валидации при изменении кода
        if (validationResult) {
            clearValidationResult();
        }
    };

    return (
        <div style={{
            position: 'fixed',
            width: r.vmax(1226),
            left: r.vmax(12),
            top: r.vmax(22),
            bottom: r.vmax(24),
        }}>
            {svgJSX}
            {/*{svgJSXBorder}*/}
            {/*<DisabledWrapper style={{*/}
            {/*    zIndex: 9999,*/}
            {/*}}>*/}
            {/*    <div ref={editorBorderRef} style={{*/}
            {/*        position: 'absolute',*/}
            {/*        inset: 16,*/}
            {/*        background: 'red',*/}
            {/*        clipPath: clipPathBorder,*/}
            {/*    }}></div>*/}
            {/*</DisabledWrapper>*/}
            {/*<svg style={{*/}
            {/*    width: r.vmax(687),*/}
            {/*    height: r.vmax(45),*/}
            {/*    position: 'absolute',*/}
            {/*    top: 0,*/}
            {/*    pointerEvents: 'none',*/}
            {/*    userSelect: 'none',*/}
            {/*    left: r.vmax(198),*/}
            {/*}} viewBox="0 0 687 45" fill="none" xmlns="http://www.w3.org/2000/svg">*/}
            {/*    <path*/}
            {/*        d="M542.5 1.00349e-05V4.00001H203.347C190.346 4.00001 184.279 4.02261 178.692 5.81251C176.843 6.40497 175.042 7.13815 173.305 8.00587C168.057 10.6274 163.7 14.8496 154.397 23.9307C145.276 32.8354 140.419 37.6064 134.482 40.5723C132.563 41.5313 130.572 42.3423 128.528 42.9971C122.208 45.0221 115.401 45 102.653 45H0V41H102.653C115.654 41 121.721 40.9774 127.308 39.1875C129.157 38.5951 130.958 37.8619 132.695 36.9942C137.943 34.3726 142.3 30.1504 151.603 21.0693C160.724 12.1646 165.581 7.39362 171.518 4.42774C173.437 3.4687 175.428 2.65776 177.472 2.00294C183.792 -0.0220332 190.599 9.59298e-06 203.347 1.00349e-05H542.5Z"*/}
            {/*        fill="#5383FF"/>*/}
            {/*    <path*/}
            {/*        d="M610.5 1.00349e-05V4.00001H271.347C258.346 4.00001 252.279 4.02261 246.692 5.81251C244.843 6.40497 243.042 7.13815 241.305 8.00587C236.057 10.6274 231.7 14.8496 222.397 23.9307C213.276 32.8354 208.419 37.6064 202.482 40.5723C200.563 41.5313 198.572 42.3423 196.528 42.9971C190.208 45.0221 183.401 45 170.653 45H68V41H170.653C183.654 41 189.721 40.9774 195.308 39.1875C197.157 38.5951 198.958 37.8619 200.695 36.9942C205.943 34.3726 210.3 30.1504 219.603 21.0693C228.724 12.1646 233.581 7.39362 239.518 4.42774C241.437 3.4687 243.428 2.65776 245.472 2.00294C251.792 -0.0220332 258.599 9.59298e-06 271.347 1.00349e-05H610.5Z"*/}
            {/*        fill="#5383FF"/>*/}
            {/*    <path*/}
            {/*        d="M686.5 1.00349e-05V4.00001H347.347C334.346 4.00001 328.279 4.02261 322.692 5.81251C320.843 6.40497 319.042 7.13815 317.305 8.00587C312.057 10.6274 307.7 14.8496 298.397 23.9307C289.276 32.8354 284.419 37.6064 278.482 40.5723C276.563 41.5313 274.572 42.3423 272.528 42.9971C266.208 45.0221 259.401 45 246.653 45H144V41H246.653C259.654 41 265.721 40.9774 271.308 39.1875C273.157 38.5951 274.958 37.8619 276.695 36.9942C281.943 34.3726 286.3 30.1504 295.603 21.0693C304.724 12.1646 309.581 7.39362 315.518 4.42774C317.437 3.4687 319.428 2.65776 321.472 2.00294C327.792 -0.0220332 334.599 9.59298e-06 347.347 1.00349e-05H686.5Z"*/}
            {/*        fill="#5383FF"/>*/}
            {/*</svg>*/}
            <div
                {...props}
                ref={editorRef}
                style={{
                    position: 'absolute',
                    paddingTop: r.vmax(80),
                    background: '#110B29',
                    clipPath: clipPath,
                    width: '100%',
                    height: '100%',
                    ...props.style,
                }}
            >
                <EditorBlurShapes/>
                <DisabledWrapper style={{
                    zIndex: 1,
                    backgroundImage: `url(${gridPattern})`,
                    backgroundSize: r.vmax(101),
                    backgroundRepeat: 'repeat',
                    backgroundPosition: 'center center',
                    mixBlendMode: 'color-dodge'
                }}>
                </DisabledWrapper>
                <BotHelperButton hint={currentTask?.hint}/>
                <div style={{
                    position: 'relative',
                    inset: 0,
                    zIndex: 3,
                    width: '100%',
                    height: '100%',
                    marginLeft: r.vmax(-10),
                }}>
                    <Editor
                        height='100%'
                        language="yaml"
                        value={currentYaml || ''}
                        onChange={handleEditorChange}
                        options={{
                            minimap: {enabled: false},
                            tabSize: 2,
                            insertSpaces: true,
                            detectIndentation: false,
                            renderValidationDecorations: 'on',
                            glyphMargin: true,
                            overviewRulerLanes: 3,
                            fontSize: window.innerWidth / 1920 * 20,
                            lineHeight: window.innerWidth / 1920 * 30,
                            lineNumbers: 'on',
                            wordWrap: 'on',
                            automaticLayout: true,
                            scrollbar: {
                                vertical: 'hidden',
                                horizontal: 'hidden',
                            },
                            // оставляем обзорные линейки включенными выше
                            scrollBeyondLastLine: false,
                            scrollBeyondLastColumn: 0,
                            stickyScroll: {enabled: false},
                            renderLineHighlight: 'none',
                            guides: {
                                indentation: true,
                                highlightActiveIndentation: true,
                            },
                            find: {
                                addExtraSpaceOnTop: false,
                                autoFindInSelection: 'never',
                                seedSearchStringFromSelection: 'never',
                            },
                            quickSuggestions: false,
                            parameterHints: {enabled: false},
                            suggest: {showKeywords: false, showSnippets: false},
                        }}
                        theme="vs-dark"
                        beforeMount={(monaco) => {
                            monacoRef.current = monaco;

                            // Suppress AbortError from media elements
                            if (typeof window !== 'undefined') {
                                const originalConsoleError = console.error;
                                console.error = (...args) => {
                                    const message = args.join(' ');
                                    if (message.includes('AbortError') && message.includes('play()')) {
                                        return; // Suppress AbortError for media play()
                                    }
                                    originalConsoleError.apply(console, args);
                                };
                            }

                            // Configure Monaco Environment for Web Workers
                            // Removed custom MonacoEnvironment worker configuration

                            // Configure K8s field completion for YAML
                            console.log('Setting up K8s YAML validation');

                            // Register custom completion provider for YAML
                            // monaco.languages.registerCompletionItemProvider('yaml', {
                            //     provideCompletionItems: (model, position) => {
                            //         const word = model.getWordUntilPosition(position);
                            //         const range = {
                            //             startLineNumber: position.lineNumber,
                            //             endLineNumber: position.lineNumber,
                            //             startColumn: word.startColumn,
                            //             endColumn: word.endColumn
                            //         };
                            //
                            //         // K8s field suggestions
                            //         const suggestions = [
                            //             { label: 'apiVersion', kind: monaco.languages.CompletionItemKind.Field, insertText: 'apiVersion: ', range },
                            //             { label: 'kind', kind: monaco.languages.CompletionItemKind.Field, insertText: 'kind: ', range },
                            //             { label: 'metadata', kind: monaco.languages.CompletionItemKind.Field, insertText: 'metadata:\n  ', range },
                            //             { label: 'spec', kind: monaco.languages.CompletionItemKind.Field, insertText: 'spec:\n  ', range },
                            //             { label: 'replicas', kind: monaco.languages.CompletionItemKind.Field, insertText: 'replicas: ', range },
                            //             { label: 'selector', kind: monaco.languages.CompletionItemKind.Field, insertText: 'selector:\n  ', range },
                            //             { label: 'template', kind: monaco.languages.CompletionItemKind.Field, insertText: 'template:\n  ', range },
                            //             { label: 'containers', kind: monaco.languages.CompletionItemKind.Field, insertText: 'containers:\n  - ', range },
                            //             { label: 'name', kind: monaco.languages.CompletionItemKind.Field, insertText: 'name: ', range },
                            //             { label: 'image', kind: monaco.languages.CompletionItemKind.Field, insertText: 'image: ', range },
                            //             { label: 'ports', kind: monaco.languages.CompletionItemKind.Field, insertText: 'ports:\n  - ', range },
                            //             { label: 'containerPort', kind: monaco.languages.CompletionItemKind.Field, insertText: 'containerPort: ', range },
                            //         ];
                            //
                            //         return { suggestions };
                            //     }
                            // });
                            // setupK8sYamlValidation(monaco)
                            configureMonacoYaml(monaco, {
                                isKubernetes: true,
                                // enableSchemaRequest: true,
                                schemas: [
                                    {
                                        uri: "https://kubernetesjsonschema.dev/v1.14.0/deployment-apps-v1.json",
                                        fileMatch: ["*"]
                                    },
                                    {
                                        uri: "https://kubernetesjsonschema.dev/v1.10.3-standalone/service-v1.json",
                                        fileMatch: ["*"]
                                    }
                                ]
                            })

                            monaco.editor.defineTheme('transparent', {
                                base: 'vs-dark',
                                inherit: true,
                                rules: [
                                    {token: 'string', foreground: 'F28C70'},
                                    {token: 'key', foreground: '23E0BE'},
                                    {token: 'keyword', foreground: '23E0BE'},
                                    {token: 'number', foreground: 'F28C70'},
                                    {token: 'comment', foreground: '666666'},
                                ],
                                colors: {
                                    'editor.background': '#00000000',
                                    'editor.foreground': '#FFFFFF',
                                    'editorLineNumber.foreground': '#B9BBD1',
                                    'editorLineNumber.activeForeground': '#B9BBD1',
                                    'focusBorder': '#00000000',
                                    'editor.selectionBackground': '#264F78',
                                    'editor.selectionHighlightBackground': '#ADD6FF26',
                                    'editor.lineHighlightBackground': '#00000000',
                                    'editor.lineHighlightBorder': '#00000000',
                                    'editorIndentGuide.background': '#525A8A',
                                    'editorIndentGuide.activeBackground': '#525A8A',
                                    'editorIndentGuide.background1': '#525A8A',
                                    'editorIndentGuide.background2': '#525A8A',
                                    'editorIndentGuide.background3': '#525A8A',
                                    'editorIndentGuide.background4': '#525A8A',
                                    'editorIndentGuide.background5': '#525A8A',
                                    'editorIndentGuide.background6': '#525A8A',
                                    'tree.indentGuidesStroke': '#525A8A',
                                    'editorError.foreground': '#FF5252',
                                    'editorWarning.foreground': '#FFA726',
                                    'editorMarkerNavigation.background': '#2D2D30',
                                    'editorMarkerNavigationError.background': '#FF5252',
                                    'editorMarkerNavigationWarning.background': '#FFA726',
                                }
                            });
                        }}
                        onMount={(editor, monaco) => {
                            editorInstanceRef.current = editor;
                            monaco.editor.setTheme('transparent');

                            // Отключаем горячие клавиши поиска и command palette
                            editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF, () => {
                            });
                            editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyP, () => {
                            });
                            editor.addCommand(monaco.KeyCode.F1, () => {
                            });

                            // Отключаем Ctrl+Enter в редакторе, чтобы наш обработчик мог его перехватить
                            editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, async () => {
                                const { currentYaml, currentTask, isLoading, submitSolution } = submitRef.current as any;
                                if (currentYaml && currentTask && !isLoading && typeof submitSolution === 'function') {
                                    await submitSolution(currentYaml);
                                }
                            });

                            // Вызов подсказки при Ctrl/Cmd+I, даже когда фокус в Monaco
                            editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyI, () => {
                                const evt = new KeyboardEvent('keydown', { key: 'i', ctrlKey: true, metaKey: true, bubbles: true });
                                window.dispatchEvent(evt);
                            });

                            const runValidation = () => {
                                const model = editor.getModel();
                                if (!model) return;
                                const value = model.getValue();
                                const lines = value.split(/\r?\n/);

                                const markers: any[] = [];
                                const decorations: any[] = [];

                                // Try to parse YAML and validate K8s schema
                                let yamlObj: any = null;
                                try {
                                    yamlObj = load(value);
                                } catch (e) {
                                    // YAML parse error will be handled later
                                }

                                // If we have a valid YAML object, validate K8s structure
                                if (yamlObj && typeof yamlObj === 'object') {
                                    // Basic K8s schema validation
                                    if (yamlObj.kind === 'Deployment') {
                                        // Check required fields for Deployment
                                        if (!yamlObj.apiVersion) {
                                            markers.push({
                                                severity: monaco.MarkerSeverity.Error,
                                                message: 'Missing required field: apiVersion (e.g., "apps/v1")',
                                                startLineNumber: 1,
                                                startColumn: 1,
                                                endLineNumber: 1,
                                                endColumn: 1,
                                            });
                                        }

                                        if (!yamlObj.metadata) {
                                            markers.push({
                                                severity: monaco.MarkerSeverity.Error,
                                                message: 'Missing required field: metadata',
                                                startLineNumber: 1,
                                                startColumn: 1,
                                                endLineNumber: 1,
                                                endColumn: 1,
                                            });
                                        }

                                        if (!yamlObj.spec) {
                                            markers.push({
                                                severity: monaco.MarkerSeverity.Error,
                                                message: 'Missing required field: spec',
                                                startLineNumber: 1,
                                                startColumn: 1,
                                                endLineNumber: 1,
                                                endColumn: 1,
                                            });
                                        } else {
                                            // Check spec fields
                                            if (yamlObj.spec.replicas !== undefined && typeof yamlObj.spec.replicas !== 'number') {
                                                // Find the line with replicas
                                                for (let i = 0; i < lines.length; i++) {
                                                    if (lines[i].includes('replicas:')) {
                                                        markers.push({
                                                            severity: monaco.MarkerSeverity.Error,
                                                            message: 'Field "replicas" must be a number',
                                                            startLineNumber: i + 1,
                                                            startColumn: 1,
                                                            endLineNumber: i + 1,
                                                            endColumn: lines[i].length + 1,
                                                        });
                                                        break;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }

                                // K8s common fields validation - expanded list
                                const k8sCommonFields = [
                                    'apiVersion', 'kind', 'metadata', 'spec', 'status',
                                    'name', 'namespace', 'labels', 'annotations',
                                    'replicas', 'selector', 'template', 'containers',
                                    'image', 'ports', 'env', 'resources', 'limits', 'requests',
                                    'volumeMounts', 'volumes', 'strategy', 'type',
                                    'matchLabels', 'containerPort', 'protocol', 'cpu', 'memory',
                                    'revisionHistoryLimit', 'progressDeadlineSeconds', 'minReadySeconds',
                                    'paused', 'imagePullPolicy', 'command', 'args', 'workingDir',
                                    'envFrom', 'lifecycle', 'terminationMessagePath', 'terminationMessagePolicy',
                                    'imagePullSecrets', 'securityContext', 'stdin', 'stdinOnce', 'tty'
                                ];

                                // Check for typos in common K8s fields
                                for (let i = 0; i < lines.length; i++) {
                                    const line = lines[i];
                                    const fieldMatch = line.match(/^(\s*)([a-zA-Z0-9а-яА-Я]+):/);
                                    if (fieldMatch) {
                                        const indent = fieldMatch[1];
                                        const field = fieldMatch[2];

                                        // Check if field contains cyrillic characters (common typo)
                                        if (/[а-яА-Я]/.test(field)) {
                                            markers.push({
                                                severity: monaco.MarkerSeverity.Error,
                                                message: `Кириллица в названии поля: "${field}". Используйте латиницу.`,
                                                startLineNumber: i + 1,
                                                startColumn: indent.length + 1,
                                                endLineNumber: i + 1,
                                                endColumn: indent.length + field.length + 1,
                                            });
                                        }

                                        // Check for common typos using simple similarity check
                                        const fieldLower = field.toLowerCase();
                                        let closestMatch = '';
                                        let minDifference = Infinity;

                                        // Find the closest matching K8s field
                                        for (const knownField of k8sCommonFields) {
                                            const knownLower = knownField.toLowerCase();

                                            // Check if it's an exact match (correct field)
                                            if (fieldLower === knownLower) {
                                                closestMatch = '';
                                                break;
                                            }

                                            // Simple similarity: check if one contains the other or very similar
                                            if (fieldLower.length > 2 && knownLower.length > 2) {
                                                // Calculate simple difference metric
                                                const lengthDiff = Math.abs(fieldLower.length - knownLower.length);
                                                if (lengthDiff <= 3) {
                                                    // Check if they start similarly
                                                    let commonStart = 0;
                                                    for (let j = 0; j < Math.min(fieldLower.length, knownLower.length); j++) {
                                                        if (fieldLower[j] === knownLower[j]) {
                                                            commonStart++;
                                                        } else {
                                                            break;
                                                        }
                                                    }

                                                    // If they share at least 60% of starting characters
                                                    if (commonStart >= Math.min(fieldLower.length, knownLower.length) * 0.6) {
                                                        const diff = lengthDiff + (Math.min(fieldLower.length, knownLower.length) - commonStart);
                                                        if (diff < minDifference) {
                                                            minDifference = diff;
                                                            closestMatch = knownField;
                                                        }
                                                    }
                                                }
                                            }
                                        }

                                        // If we found a likely typo
                                        if (closestMatch && minDifference <= 3) {
                                            markers.push({
                                                severity: monaco.MarkerSeverity.Warning,
                                                message: `Возможная опечатка. Вы имели в виду "${closestMatch}"?`,
                                                startLineNumber: i + 1,
                                                startColumn: indent.length + 1,
                                                endLineNumber: i + 1,
                                                endColumn: indent.length + field.length + 1,
                                            });
                                        }
                                        // If field is completely unknown and not a valid YAML structure keyword
                                        else if (!closestMatch && !['items', 'properties', 'type', 'value', 'key', 'path', 'host'].includes(fieldLower)) {
                                            // Check if it's under spec or metadata (common areas for K8s fields)
                                            const previousLines = lines.slice(Math.max(0, i - 5), i).join('\\n');
                                            if (previousLines.includes('spec:') || previousLines.includes('metadata:')) {
                                                markers.push({
                                                    severity: monaco.MarkerSeverity.Info,
                                                    message: `Неизвестное поле "${field}" для Kubernetes объекта`,
                                                    startLineNumber: i + 1,
                                                    startColumn: indent.length + 1,
                                                    endLineNumber: i + 1,
                                                    endColumn: indent.length + field.length + 1,
                                                });
                                            }
                                        }
                                    }
                                }

                                // Подсветка табов (в YAML недопустимы)
                                // Проверяем каждый символ для точного определения табов
                                let lineNumber = 1;
                                let column = 1;
                                for (let i = 0; i < value.length; i++) {
                                    const char = value[i];

                                    if (char === '\t') {
                                        // Нашли таб
                                        markers.push({
                                            severity: monaco.MarkerSeverity.Error,
                                            message: 'Табуляция запрещена в YAML. Используйте пробелы.',
                                            startLineNumber: lineNumber,
                                            startColumn: column,
                                            endLineNumber: lineNumber,
                                            endColumn: column + 1,
                                        });
                                        decorations.push({
                                            range: new monaco.Range(lineNumber, column, lineNumber, column + 1),
                                            options: {
                                                className: 'tab-error-highlight',
                                                isWholeLine: false,
                                                overviewRuler: {
                                                    color: '#FF5252',
                                                    position: monaco.editor.OverviewRulerLane.Full
                                                },
                                                minimap: {
                                                    color: '#FF5252',
                                                    position: monaco.editor.MinimapPosition.Inline
                                                },
                                                hoverMessage: {value: '**Ошибка:** Табуляция запрещена в YAML'}
                                            }
                                        });
                                    }

                                    if (char === '\n') {
                                        lineNumber++;
                                        column = 1;
                                    } else {
                                        column++;
                                    }
                                }

                                // Дополнительная простая проверка: смешение табов и пробелов в начале строки
                                for (let i = 0; i < lines.length; i++) {
                                    const line = lines[i];
                                    const match = line.match(/^(\s+)/);
                                    if (match) {
                                        const indent = match[1];
                                        if (indent.includes('\t')) {
                                            markers.push({
                                                severity: monaco.MarkerSeverity.Error,
                                                message: 'Недопустимая табуляция в отступах. Используйте только пробелы.',
                                                startLineNumber: i + 1,
                                                startColumn: 1,
                                                endLineNumber: i + 1,
                                                endColumn: indent.length + 1,
                                            });
                                        } else if (/^ +$/.test(indent) && (indent.length % 2 !== 0)) {
                                            markers.push({
                                                severity: monaco.MarkerSeverity.Warning,
                                                message: 'Некаратный отступ. Используйте кратно 2 пробелам.',
                                                startLineNumber: i + 1,
                                                startColumn: 1,
                                                endLineNumber: i + 1,
                                                endColumn: indent.length + 1,
                                            });
                                        }
                                    }
                                }

                                // Парсинг YAML для подсветки синтаксических ошибок
                                try {
                                    load(value);
                                } catch (e: any) {
                                    if (e && e.mark && typeof e.mark.line === 'number' && typeof e.mark.column === 'number') {
                                        markers.push({
                                            severity: monaco.MarkerSeverity.Error,
                                            message: e.reason || 'Ошибка синтаксиса YAML',
                                            startLineNumber: e.mark.line + 1,
                                            startColumn: e.mark.column + 1,
                                            endLineNumber: e.mark.line + 1,
                                            endColumn: e.mark.column + 2,
                                        });
                                    }
                                }

                                monaco.editor.setModelMarkers(model, 'yaml-validate', markers);

                                // Применяем декорации
                                decorationsRef.current = editor.deltaDecorations(decorationsRef.current, decorations);

                                console.log('Валидация YAML:', markers.length, 'ошибок найдено');

                                // Дополнительная диагностика для табов
                                const tabCount = (value.match(/\t/g) || []).length;
                                if (tabCount > 0) {
                                    console.log('Найдено табов:', tabCount);
                                }
                            };

                            // Первая валидация и реакция на изменения
                            // runValidation();
                            // editor.onDidChangeModelContent(() => runValidation());

                            // Добавляем CSS для подсветки табов
                            const style = document.createElement('style');
                            style.textContent = `
                                .monaco-editor .view-overlays .tab-error-highlight {
                                    background-color: rgba(255, 82, 82, 0.5) !important;
                                    box-shadow: inset 0 0 0 1px #FF5252 !important;
                                }
                                .monaco-editor .margin-view-overlays .tab-error-decoration::before {
                                    content: '⇥';
                                    color: #FF5252;
                                    font-weight: bold;
                                    position: absolute;
                                    left: 0;
                                }
                                .monaco-editor .squiggly-error {
                                    background: url("data:image/svg+xml,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%206%203'%20enable-background%3D'new%200%200%206%203'%20height%3D'3'%20width%3D'6'%3E%3Cg%20fill%3D'%23ff5252'%3E%3Cpolygon%20points%3D'5.5%2C0%202.5%2C3%201.1%2C3%204.1%2C0'%2F%3E%3Cpolygon%20points%3D'4%2C0%206%2C2%206%2C0.6%205.4%2C0'%2F%3E%3Cpolygon%20points%3D'0%2C2%201%2C3%202.4%2C3%200%2C0.6'%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E") repeat-x bottom left !important;
                                    padding-bottom: 3px;
                                }
                            `;
                            document.head.appendChild(style);

                            // Включаем отображение невидимых символов для табов
                            editor.updateOptions({
                                renderWhitespace: 'selection',
                                renderControlCharacters: true
                            });
                        }}
                    />
                </div>

                {/* Показываем результат валидации */}
                {validationResult && showValidationResult && (
                    <ValidationResult
                        result={validationResult}
                        onClose={() => setShowValidationResult(false)}
                        onNextTask={validationResult.success ? handleNextTask : undefined}
                    />
                )}
            </div>
        </div>
    );
};

export default CodeEditor;
